import React, { useCallback, useMemo, useState } from 'react';
import { WorkflowCanvas } from './WorkflowCanvas';
import {
  NodeTemplate,
  WorkflowCanvasState,
  WorkflowNode as WorkflowNodeType,
  WorkflowConnection,
} from '../../types/workflow';
import { AI_AGENTS_CATALOG } from '../../constants/agents';

interface WorkflowBuilderProps {
  onSave?: (workflow: { name: string; nodes: WorkflowNodeType[]; connections: WorkflowConnection[] }) => void;
  onRun?: (workflow: { name: string; nodes: WorkflowNodeType[]; connections: WorkflowConnection[] }) => void;
}

const DEFAULT_TEMPLATES: NodeTemplate[] = [
  {
    type: 'trigger',
    name: 'Start',
    description: 'Workflow start node',
    icon: '▶️',
    category: 'triggers',
    color: '#16a34a',
    inputs: [],
    outputs: [{ id: 'out', name: 'start', type: 'object' }],
    defaultData: {},
  },
  {
    type: 'trigger',
    name: 'Email Trigger',
    description: 'Starts when a new email arrives',
    icon: '✉️',
    category: 'triggers',
    color: '#1e3a8a',
    inputs: [],
    outputs: [{ id: 'out', name: 'onEmail', type: 'object' }],
    defaultData: {},
  },
  {
    type: 'condition',
    name: 'Condition',
    description: 'Branch flow based on a boolean expression',
    icon: '🔀',
    category: 'logic',
    color: '#f59e0b',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [
      { id: 'true', name: 'true', type: 'object' },
      { id: 'false', name: 'false', type: 'object' }
    ],
    defaultData: { expression: 'payload.flag === true' },
  },
  {
    type: 'delay',
    name: 'End',
    description: 'Workflow end node',
    icon: '⏹️',
    category: 'utilities',
    color: '#ef4444',
    inputs: [{ id: 'in', name: 'end', type: 'object' }],
    outputs: [],
    defaultData: {},
  },
];

const AGENT_TEMPLATES: NodeTemplate[] = AI_AGENTS_CATALOG.map((agent) => ({
  type: 'ai-agent',
  name: agent.name,
  description: agent.description,
  icon: agent.icon,
  category: 'ai',
  color: '#3b82f6',
  inputs: [{ id: 'in', name: 'input', type: 'object' }],
  outputs: [{ id: 'out', name: 'output', type: 'object' }],
  defaultData: { agentId: agent.id },
}));

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({ onSave, onRun }) => {
  const [name, setName] = useState('Untitled Workflow');
  const [canvasState, setCanvasState] = useState<WorkflowCanvasState>({
    nodes: [],
    connections: [],
    isConnecting: false,
  });

  const handleUpdateCanvasState = useCallback((updates: Partial<WorkflowCanvasState>) => {
    setCanvasState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleAddNode = useCallback((template: NodeTemplate, position?: { x: number; y: number }) => {
    const newNode: WorkflowNodeType = {
      id: `node-${Date.now()}`,
      type: template.type,
      name: template.name,
      position: position ?? { x: 40, y: 40 },
      data: { ...template.defaultData },
      inputs: template.inputs,
      outputs: template.outputs,
      status: 'idle',
    };
    setCanvasState((prev) => ({ ...prev, nodes: [...prev.nodes, newNode] }));
  }, []);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<WorkflowNodeType>) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)),
    }));
  }, []);

  const handleDeleteNode = useCallback((nodeId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.filter((n) => n.id !== nodeId),
      connections: prev.connections.filter((c) => c.sourceNodeId !== nodeId && c.targetNodeId !== nodeId),
    }));
  }, []);

  const handleAddConnection = useCallback((connection: WorkflowConnection) => {
    setCanvasState((prev) => ({ ...prev, connections: [...prev.connections, connection] }));
  }, []);

  const handleDeleteConnection = useCallback((connectionId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      connections: prev.connections.filter((c) => c.id !== connectionId),
    }));
  }, []);

  const onDragStartTemplate = useCallback((template: NodeTemplate) => (e: React.DragEvent) => {
    const payload = JSON.stringify(template);
    try {
      e.dataTransfer.setData('application/json', payload);
    } catch {}
    // Fallback for Safari and strict browsers
    try {
      e.dataTransfer.setData('text/plain', payload);
    } catch {}
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const workflowPayload = useMemo(() => ({
    name,
    nodes: canvasState.nodes,
    connections: canvasState.connections,
  }), [name, canvasState.nodes, canvasState.connections]);

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* Sidebar Palette */}
      <aside className="w-72 bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Nodes</h2>
          <span className="text-xs text-gray-500">{[...DEFAULT_TEMPLATES, ...AGENT_TEMPLATES].length}</span>
        </div>
        <div className="space-y-3 overflow-y-auto pr-1 mt-3" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
          {[...DEFAULT_TEMPLATES, ...AGENT_TEMPLATES].map((tpl) => (
            <div
              key={tpl.name}
              className="p-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={onDragStartTemplate(tpl)}
              title={tpl.description}
            >
              <div className="flex items-center justify-between">
                <div className="text-gray-900 text-sm font-medium">{tpl.icon} {tpl.name}</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{tpl.category}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500 break-words">{tpl.description}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main Builder Area */}
      <div className="flex-1 flex flex-col gap-3">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
          <input
            className="flex-1 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workflow name"
          />
          <button
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={() => onSave?.(workflowPayload)}
          >
            Save
          </button>
          <button
            className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            onClick={() => onRun?.(workflowPayload)}
          >
            Run
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 min-h-[600px]">
          <WorkflowCanvas
            canvasState={canvasState}
            onUpdateCanvasState={handleUpdateCanvasState}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onAddConnection={handleAddConnection}
            onDeleteConnection={handleDeleteConnection}
            onAddNode={handleAddNode}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;


